import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calculator } from 'lucide-react';

export default function SimulateurAmzCosmFr() {
  // Paramètres globaux
  const [commission, setCommission] = useState(15);
  const [fraisFBA, setFraisFBA] = useState(3);

  // Calculateur 1: Calcul de rentabilité
  const [prixFournisseur, setPrixFournisseur] = useState<number>(0);
  const [pvcAmazon, setPvcAmazon] = useState<number>(0);
  const [resultRentabilite, setResultRentabilite] = useState<any>(null);

  // Calculateur 2: Calcul du prix cible
  const [pvcTarget, setPvcTarget] = useState<number>(0);
  const [margeCible, setMargeCible] = useState<number>(35);
  const [resultPrixCible, setResultPrixCible] = useState<any>(null);

  const calculerRentabilite = () => {
    if (!prixFournisseur || !pvcAmazon) {
      alert("Veuillez remplir tous les champs avec des valeurs valides.");
      return;
    }

    // Calculs entièrement en euros (marché français)
    const prixHT = pvcAmazon / 1.20; // Conversion TTC vers HT (TVA 20% France)
    const fraisCommission = prixHT * (commission / 100);
    const fraisTotal = fraisCommission + fraisFBA;
    const revenusVendeur = prixHT - fraisTotal;

    // Calcul du profit net et taux de marge
    const profitNet = revenusVendeur - prixFournisseur;
    const tauxMarge = revenusVendeur > 0 ? (profitNet / revenusVendeur) * 100 : 0;

    setResultRentabilite({
      prixFournisseur,
      pvcTTC: pvcAmazon,
      prixHT,
      fraisCommission,
      fraisFBA,
      revenusVendeur,
      profitNet,
      tauxMarge,
      isRentable: profitNet > 0
    });
  };

  const calculerPrixCible = () => {
    if (!pvcTarget || !margeCible) {
      alert("Veuillez remplir tous les champs avec des valeurs valides.");
      return;
    }

    if (margeCible < 0 || margeCible >= 100) {
      alert("La marge doit être entre 0% et 99%.");
      return;
    }

    // Calculs à rebours entièrement en euros (marché français)
    const prixHT = pvcTarget / 1.20; // Conversion TTC vers HT (TVA 20% France)
    const fraisCommission = prixHT * (commission / 100);
    const fraisTotal = fraisCommission + fraisFBA;
    const revenusVendeur = prixHT - fraisTotal;

    if (revenusVendeur <= 0) {
      alert("Impossible : les frais Amazon dépassent le prix de vente HT.");
      return;
    }

    const margeSouhaiteeDecimal = margeCible / 100;
    const prixFournisseurCible = revenusVendeur * (1 - margeSouhaiteeDecimal);
    const profitEstime = revenusVendeur - prixFournisseurCible;

    if (prixFournisseurCible < 0) {
      alert("Impossible : marge trop élevée pour ce PVC.");
      return;
    }

    setResultPrixCible({
      pvcTTC: pvcTarget,
      prixHT,
      fraisCommission,
      fraisFBA,
      revenusVendeur,
      margeSouhaitee: margeCible,
      prixFournisseurCible,
      profitEstime
    });
  };

  useEffect(() => {
    if (prixFournisseur > 0 && pvcAmazon > 0) {
      calculerRentabilite();
    }
  }, [commission, fraisFBA]);

  useEffect(() => {
    if (pvcTarget > 0 && margeCible > 0) {
      calculerPrixCible();
    }
  }, [commission, fraisFBA]);

  return (
    <div className="w-full max-w-7xl mx-auto space-y-6">
      {/* En-tête */}
      <div className="text-center">
        <div className="flex justify-center">
          <Badge className="bg-transparent border-2 border-orange-500 text-orange-500 font-bold px-6 py-3 text-base shadow-lg shadow-orange-500/30 animate-pulse">
            <Calculator className="w-5 h-5 mr-2" />
            Simulateur AMZ CW.Fr
          </Badge>
        </div>
      </div>

      {/* Paramètres ajustables */}
      <Card>
        <CardHeader>
          <CardTitle className="text-[#a2cc3a]">Paramètres</CardTitle>
          <CardDescription>Ajustez les paramètres de calcul selon vos besoins</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="commission">Commission Amazon (%)</Label>
            <Input
              id="commission"
              type="number"
              value={commission}
              onChange={(e) => setCommission(parseFloat(e.target.value) || 15)}
              step="0.1"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="fraisFBA">Frais FBA (€)</Label>
            <Input
              id="fraisFBA"
              type="number"
              value={fraisFBA}
              onChange={(e) => setFraisFBA(parseFloat(e.target.value) || 3)}
              step="0.1"
            />
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Calcul de rentabilité */}
        <Card>
          <CardHeader>
            <CardTitle className="text-[#a2cc3a]">Calculer la marge</CardTitle>
            <CardDescription>
              À partir du prix fournisseur et du PVC Amazon
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="prixFournisseur">Prix fournisseur (€ HT)</Label>
              <Input
                id="prixFournisseur"
                type="number"
                placeholder="Ex: 3.50"
                step="0.01"
                value={prixFournisseur || ''}
                onChange={(e) => setPrixFournisseur(parseFloat(e.target.value) || 0)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="pvcAmazon">PVC Amazon TTC (€)</Label>
              <Input
                id="pvcAmazon"
                type="number"
                placeholder="Ex: 8.90"
                step="0.01"
                value={pvcAmazon || ''}
                onChange={(e) => setPvcAmazon(parseFloat(e.target.value) || 0)}
              />
            </div>

            <Button onClick={calculerRentabilite} className="w-full">
              Calculer la rentabilité
            </Button>

            {resultRentabilite && (
              <div className="mt-4 p-4 bg-card border border-[#a2cc3a]/30 rounded-lg space-y-2">
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="font-medium">Prix fournisseur HT:</span>
                  <span className="text-[#a2cc3a]">
                    {resultRentabilite.prixFournisseur.toFixed(2)} EUR
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="font-medium">Prix de vente HT (EUR):</span>
                  <span className="text-[#a2cc3a]">
                    {resultRentabilite.prixHT.toFixed(2)} EUR
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="font-medium">Commission Amazon:</span>
                  <span className="text-[#a2cc3a]">
                    {resultRentabilite.fraisCommission.toFixed(2)} EUR
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="font-medium">Frais FBA:</span>
                  <span className="text-[#a2cc3a]">
                    {resultRentabilite.fraisFBA.toFixed(2)} EUR
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="font-medium">Revenus vendeur:</span>
                  <span className="text-[#a2cc3a]">
                    {resultRentabilite.revenusVendeur.toFixed(2)} EUR
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="font-medium">Profit net:</span>
                  <span className={resultRentabilite.profitNet < 0 ? 'text-destructive' : 'text-[#a2cc3a]'}>
                    {resultRentabilite.profitNet.toFixed(2)} EUR
                  </span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="font-medium text-orange-500">% de marge:</span>
                  <span className="text-orange-500">
                    {resultRentabilite.tauxMarge.toFixed(1)}%
                  </span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Calcul du prix cible */}
        <Card>
          <CardHeader>
            <CardTitle className="text-[#a2cc3a]">Calculer le prix cible</CardTitle>
            <CardDescription>
              À partir du PVC Amazon et de la marge souhaitée
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="pvcTarget">PVC Amazon TTC (€)</Label>
              <Input
                id="pvcTarget"
                type="number"
                placeholder="Ex: 8.90"
                step="0.01"
                value={pvcTarget || ''}
                onChange={(e) => setPvcTarget(parseFloat(e.target.value) || 0)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="margeCible">Marge souhaitée (%)</Label>
              <Input
                id="margeCible"
                type="number"
                placeholder="Ex: 35"
                step="0.1"
                value={margeCible || ''}
                onChange={(e) => setMargeCible(parseFloat(e.target.value) || 0)}
              />
            </div>

            <Button onClick={calculerPrixCible} className="w-full">
              Calculer le prix cible
            </Button>

            {resultPrixCible && (
              <div className="mt-4 p-4 bg-card border border-[#a2cc3a]/30 rounded-lg space-y-2">
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="font-medium">Prix HT (EUR):</span>
                  <span className="text-[#a2cc3a]">
                    {resultPrixCible.prixHT.toFixed(2)} EUR
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="font-medium">Commission Amazon:</span>
                  <span className="text-[#a2cc3a]">
                    {resultPrixCible.fraisCommission.toFixed(2)} EUR
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="font-medium">Frais FBA:</span>
                  <span className="text-[#a2cc3a]">
                    {resultPrixCible.fraisFBA.toFixed(2)} EUR
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="font-medium">Revenus vendeur:</span>
                  <span className="text-[#a2cc3a]">
                    {resultPrixCible.revenusVendeur.toFixed(2)} EUR
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="font-medium">Coût fournisseur max (EUR):</span>
                  <span className="text-[#a2cc3a]">
                    {resultPrixCible.prixFournisseurCible.toFixed(2)} EUR
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="font-medium text-orange-500">Prix cible fournisseur (EUR):</span>
                  <span className="text-orange-500">
                    {resultPrixCible.prixFournisseurCible.toFixed(2)} EUR
                  </span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="font-medium">Marge réalisée:</span>
                  <span className="text-[#a2cc3a]">
                    {resultPrixCible.margeSouhaitee.toFixed(1)}%
                  </span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

    </div>
  );
}
